/**
 * VerifyEmailPage
 * 
 * Pagina che gestisce la verifica email quando l'utente clicca sul link
 * ricevuto via email dopo la registrazione.
 * 
 * URL: /verify-email?token=xxxxx
 * 
 * IMPORTANTE: Questa pagina deve funzionare sia per utenti loggati che non loggati.
 * La verifica viene eseguita immediatamente al caricamento, senza dipendere da AuthContext.
 */

import { useState, useEffect, useRef } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { CheckCircle, XCircle, Loader2, ArrowRight, LogIn } from 'lucide-react'
import axios from 'axios'
import { getCsrfToken } from '../../utils/csrf'

// Usa axios direttamente invece di api per evitare interferenze con AuthContext.
// Derivato da VITE_API_BASE_URL togliendo il suffisso /api (qui servono URL assolute
// per gli endpoint pubblici di verifica email).
const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/api$/, '')

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [status, setStatus] = useState('verifying') // verifying, success, error
  const [message, setMessage] = useState('')
  const [tenantName, setTenantName] = useState('')
  
  // Ref per evitare doppia chiamata in StrictMode
  const verificationAttempted = useRef(false)

  useEffect(() => {
    // Evita doppia esecuzione
    if (verificationAttempted.current) return
    
    if (!token) {
      setStatus('error')
      setMessage('Link non valido. Manca il token di verifica.')
      return
    }

    // Marca come tentato immediatamente
    verificationAttempted.current = true
    
    // Esegui verifica immediatamente
    verifyEmail()
  }, []) // Dipendenza vuota - esegui solo al mount

  const verifyEmail = async () => {
    try {
      setStatus('verifying')
      
      // Prova prima l'endpoint Organization (nuovo flusso Responsabile)
      // Se fallisce, prova l'endpoint Tenant (flusso Coordinatore legacy)
      let response;
      let usedEndpoint = 'organization';
      
      try {
        response = await axios.post(`${API_BASE}/api/onboarding/organization/verify-email`, { 
          token 
        }, {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: false
        });
      } catch (orgError) {
        // Se l'endpoint organization fallisce con 400/404, prova l'endpoint tenant
        if (orgError.response?.status === 400 || orgError.response?.status === 404) {
          console.log('Organization verify failed, trying tenant endpoint...');
          usedEndpoint = 'tenant';
          response = await axios.post(`${API_BASE}/api/onboarding/verify-email`, { 
            token 
          }, {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: false
          });
        } else {
          // Altro tipo di errore, rilancia
          throw orgError;
        }
      }
      
      if (response.data.success) {
        setStatus('success')
        setMessage('Email verificata con successo!')
        // Gestisci sia data.name (tenant) che data.organization.name
        const name = response.data.data?.name || 
                     response.data.data?.tenant?.name || 
                     response.data.data?.organization?.name || '';
        setTenantName(name)
        
        // Nessun aggiornamento localStorage necessario — i cookie httpOnly gestiscono la sessione
      } else {
        setStatus('error')
        setMessage(response.data.message || 'Errore durante la verifica')
      }
    } catch (err) {
      console.error('Verify email error:', err)
      setStatus('error')
      
      const errorCode = err.response?.data?.code
      const errorMessage = err.response?.data?.message
      
      if (errorCode === 'INVALID_TOKEN' || errorCode === 'INVALID_OR_EXPIRED') {
        setMessage('Il link di verifica non è valido o è già stato utilizzato.')
      } else if (errorCode === 'EXPIRED_TOKEN') {
        setMessage('Il link di verifica è scaduto. Richiedi un nuovo link dalla dashboard.')
      } else if (errorCode === 'ALREADY_VERIFIED') {
        setMessage('Email già verificata! Puoi effettuare il login.')
        setStatus('success') // Mostra come successo
      } else {
        setMessage(errorMessage || 'Si è verificato un errore durante la verifica.')
      }
    }
  }

  // Controlla se l'utente ha una sessione attiva (CSRF cookie presente = sessione attiva)
  const isLoggedIn = !!getCsrfToken()

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          
          {/* Verifying State */}
          {status === 'verifying' && (
            <>
              <div className="w-20 h-20 mx-auto bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-6">
                <Loader2 className="h-10 w-10 text-primary-600 dark:text-primary-400 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Verifica in corso...
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Stiamo verificando il tuo indirizzo email.
              </p>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Email Verificata! 🎉
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {tenantName ? (
                  <>Il tuo account per <strong>{tenantName}</strong> è ora attivo.</>
                ) : (
                  'Il tuo account è ora completamente attivo.'
                )}
              </p>
              
              {isLoggedIn ? (
                // Utente già loggato - vai alla dashboard
                <Link
                  to="/dashboard"
                  className="inline-flex items-center justify-center w-full px-6 py-3 
                           bg-primary-600 hover:bg-primary-700 text-white font-medium 
                           rounded-lg transition-colors"
                  onClick={() => {
                    // Forza refresh della pagina per aggiornare lo stato auth
                    window.location.href = '/dashboard'
                  }}
                >
                  Vai alla Dashboard
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              ) : (
                // Utente non loggato - vai al login
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center w-full px-6 py-3 
                           bg-primary-600 hover:bg-primary-700 text-white font-medium 
                           rounded-lg transition-colors"
                >
                  Accedi al tuo account
                  <LogIn className="ml-2 h-5 w-5" />
                </Link>
              )}
              
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Ora puoi accedere a tutte le funzionalità di Il Diario di Bordo.
              </p>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-6">
                <XCircle className="h-10 w-10 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Verifica non riuscita
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {message}
              </p>
              
              <div className="space-y-3">
                {isLoggedIn ? (
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center justify-center w-full px-6 py-3 
                             bg-primary-600 hover:bg-primary-700 text-white font-medium 
                             rounded-lg transition-colors"
                  >
                    Vai alla Dashboard
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center w-full px-6 py-3 
                             bg-primary-600 hover:bg-primary-700 text-white font-medium 
                             rounded-lg transition-colors"
                  >
                    Vai al Login
                  </Link>
                )}
                
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Se hai già verificato l'email, puoi accedere normalmente.
                  <br />
                  Altrimenti, accedi e richiedi un nuovo link di verifica.
                </p>
              </div>
            </>
          )}

        </div>
        
        {/* Footer */}
        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          Hai bisogno di aiuto?{' '}
          <a href="mailto:supporto@ildiariodibordo.it" className="text-primary-600 hover:underline">
            Contattaci
          </a>
        </p>
      </div>
    </div>
  )
}
